import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';

import { DriversService } from '../../services/drivers.service';
import { DriverIdentificationData } from '../models/driver-identification.model';
import { ResponseInterface } from 'src/app/core/interfaces/response-interface';



export interface PanelStatus {
  loading: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class DriverIdentificationService {

  private endpoints = {
    driverImage: `${environment.apiUrl}/api/driver-images`,
    unitDriver: `${environment.apiUrl}/api/unit-drivers`,
    plateDriver: `${environment.apiUrl}/api/plate-drivers`,
    frequentDrivers: `${environment.apiUrl}/api/frequent-drivers`
  }

  public driverIdSubject = new BehaviorSubject<string | null>(null);
  driverId$ = this.driverIdSubject.asObservable();

  private openPanel = new BehaviorSubject<boolean>(false);
  openPanel$ = this.openPanel.asObservable();

  public eventUuid = null
  private driverIdentificationDataSubject = new BehaviorSubject<DriverIdentificationData[]>([]);
  driverIdentificationData$ = this.driverIdentificationDataSubject.asObservable();

  private panelStatusSubject = new BehaviorSubject<PanelStatus>({ loading: true, error: null });
  panelStatus$ = this.panelStatusSubject.asObservable();

  constructor(
    private driversService: DriversService,
    private http: HttpClient,
  ) { }


  public canIdentifyDriverEvent(slug: string): boolean {
    return slug.endsWith('360');
  }

  getDriverOriginById(id: string): string {
    if (!id || id == "0") return "unknown";
    const driver = this.getDriversValue().find(d => d.id === id);
    if (!driver) return "manual";

    const states: string[] = [];

    if (driver.fromEvent) states.push("event");
    if (driver.enrolled) states.push("enrolled");
    if (driver.withChecklist) states.push("checklist");
    if (driver.evaluated) states.push("evaluated");

    if (states.length === 0) return "unknown";

    return states.join("-");
  }

  // getters
  getDriversValue(): DriverIdentificationData[] {
    return this.driverIdentificationDataSubject.value;
  }

  getCurrentDriverId(uuid_event: string): string | null {
    if (this.eventUuid !== uuid_event) return null;
    return this.driverIdSubject.value;
  }


  reset(uuid_event?: string) {
    if (uuid_event == this.eventUuid) {
      this.openPanel.next(false);
      return;
    } else if (uuid_event) {
      this.eventUuid == uuid_event;
      this.openPanel.next(true);
    } else {
      this.openPanel.next(false);
      this.eventUuid = null;
    }
    this.driverIdSubject.next(null);
    this.driverIdentificationDataSubject.next([]);
    this.panelStatusSubject.next({ loading: true, error: null });
  }

  close() {
    this.eventUuid = null;
    this.openPanel.next(false);
    this.driverIdSubject.next(null);
    this.driverIdentificationDataSubject.next([]);
    this.panelStatusSubject.next({ loading: false, error: null });
  }

  resetDriverId() {
    this.driverIdSubject.next(null);
  }

  selectDriver(driver: DriverIdentificationData) {
    const current = this.driverIdSubject.value;
    this.driverIdSubject.next(current === driver.id ? null : driver.id);
  }
  selectDriverById(driverId: number) {
    const current = this.driverIdSubject.value;
    this.driverIdSubject.next(current === driverId.toString() ? null : driverId.toString());
  }

  toggleOpenPanel() {
    const current = this.openPanel.value;
    this.openPanel.next(!current);
  }

  closeDriverIdentificationPanel() {
    this.toggleOpenPanel();
    // this.driverIdentificationDataSubject.next([]);
    this.panelStatusSubject.next({ loading: false, error: null });
  }


  async openDriverIdentificationPanel(data: any) {
    if (!data.uuid_event) return;

    if (this.eventUuid === data.uuid_event) {
      this.toggleOpenPanel();
      return;
    }
    this.eventUuid = data.uuid_event;
    this.driverIdSubject.next(null); // resetear driver seleccionado
    // Indicar que empieza la carga
    this.panelStatusSubject.next({ loading: true, error: null });
    this.openPanel.next(true); // abrir panel

    const driverMap = new Map<string, DriverIdentificationData>();

    try {
      const driverId: number | undefined = data.driver_id;
      const unitCode = data.nombre_objeto;
      const parts = unitCode.split(" ");
      const plate = parts[parts.length - 1];

      let dateTracker = data.fecha_tracker.split(" ")[0].replaceAll("/", "") || "";
      // Ejecutar fetch en paralelo para mejorar performance
      const [driverDefault, enrolledDrivers, checklistDrivers, evaluatedDrivers] = await Promise.all([
        driverId ? this.fetchDriverIdentificationData(driverId) : Promise.resolve(null),
        unitCode ? this.fetchDriverEnrolledDataByUnit(data.imei) : Promise.resolve([]),
        plate ? this.fetchDriverChecklistDataByPlate(plate, dateTracker) : Promise.resolve([]),
        data.imei ? this.fetchDriverEvaluatedData(data.imei) : Promise.resolve([])
      ]);

      enrolledDrivers.sort((a, b) =>
        a.name.trim().localeCompare(b.name.trim(), 'es', {
          sensitivity: 'base'
        })
      );

      checklistDrivers.sort((a, b) =>
        a.name.trim().localeCompare(b.name.trim(), 'es', {
          sensitivity: 'base'
        })
      );

      evaluatedDrivers.sort((a, b) => {
        if (b.frequency != null && a.frequency != null && b.frequency !== a.frequency) {
          return b.frequency - a.frequency;
        }
        return a.name.trim().localeCompare(b.name.trim(), 'es', {
          sensitivity: 'base'
        });
      });


      const mergeDriver = (driver: DriverIdentificationData) => {
        if (!driverMap.has(driver.id)) {
          driverMap.set(driver.id, { ...driver });
        } else {
          const existing = driverMap.get(driver.id)!;

          // Combinar estados
          driverMap.set(driver.id, {
            ...existing,
            ...driver,
            fromEvent: existing.fromEvent || driver.fromEvent,
            enrolled: existing.enrolled || driver.enrolled,
            withChecklist: existing.withChecklist || driver.withChecklist,
            evaluated: existing.evaluated || driver.evaluated,
            checklistDate: existing.checklistDate || driver.checklistDate,
            frequency: existing.frequency ?? driver.frequency,
            lastSeen: existing.lastSeen ?? driver.lastSeen,
            default: existing.default || driver.default
          });
        }
      };

      // Agregar todos
      if (driverDefault) mergeDriver(driverDefault);
      enrolledDrivers.forEach(mergeDriver);
      checklistDrivers.forEach(mergeDriver);
      evaluatedDrivers.forEach(mergeDriver);

      // Convertir a array final
      const uniqueDrivers = Array.from(driverMap.values());

      this.driverIdentificationDataSubject.next(uniqueDrivers);
      let driverDefaultId = uniqueDrivers.find(d => d.default)?.id;
      if (driverDefaultId) this.driverIdSubject.next(driverDefaultId);
      // Carga finalizada correctamente

      this.panelStatusSubject.next({
        loading: false,
        error: uniqueDrivers.length ? null : 'No se encontraron conductores en la unidad'
      });

    } catch (error: any) {
      console.error('Error opening driver identification panel:', error);
      this.panelStatusSubject.next({ loading: false, error: 'No se pudo cargar la información' });
    }
  }

  private async fetchDriverIdentificationData(driverId: number): Promise<DriverIdentificationData | null> {
    if (!driverId) return null;

    try {
      const response = await this.http.post<ResponseInterface>(this.endpoints.driverImage, { driver_id: driverId }).toPromise();

      if (response?.data?.image) {
        return {
          id: driverId.toString(),
          name: response.data.driver_name || 'Conductor Desconocido',
          document: response.data.cod_document || "",
          image: response.data.image.startsWith('data:') ? response.data.image : `data:image/jpeg;base64,${response.data.image}`,
          fromEvent: true,
          default: true
        };
      }

    } catch (error) {
      console.error('Error fetching driver identification data:', error);
    }

    return null;
  }

  private async fetchDriverEnrolledDataByUnit(unitCode: string): Promise<DriverIdentificationData[]> {
    if (!unitCode) return [];

    try {
      const response = await this.http.post<ResponseInterface>(this.endpoints.unitDriver, { imei: unitCode }).toPromise();

      if (response?.data?.drivers?.length) {
        return response.data.drivers.map((driver: any) => ({
          id: driver.driver_id.toString(),
          name: driver.driver_name,
          document: driver.cod_document || "",
          image: driver.image ? (driver.image.startsWith('data:') ? driver.image : `data:image/jpeg;base64,${driver.image}`) : '',
          withChecklist: false,
          enrolled: true
        }));
      }

    } catch (error) {
      console.error('Error fetching enrolled drivers:', error);
    }

    return [];
  }

  private async fetchDriverChecklistDataByPlate(plate: string, date: string): Promise<DriverIdentificationData[]> {
    if (!plate) return [];
    try {
      const response = await this.http.post<ResponseInterface>(this.endpoints.plateDriver, { placa: plate, fecha: date }).toPromise();

      if (response?.data?.drivers?.length) {
        return response.data.drivers.map((driver: any) => ({
          id: driver.driver_id.toString(),
          name: driver.driver_name,
          document: driver.cod_trab,
          image: driver.image ? (driver.image.startsWith('data:') ? driver.image : `data:image/jpeg;base64,${driver.image}`) : '',
          withChecklist: true,
          enrolled: false,
          checklistDate: driver.fecha_checklist
            ? new Date(driver.fecha_checklist).toLocaleString('es-PE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })
            : '-'
        }));
      }

    } catch (error) {
      console.error('Error fetching checklist drivers:', error);
    }

    return [];
  }

  private async fetchDriverEvaluatedData(imei: string): Promise<DriverIdentificationData[]> {
    if (!imei) return [];

    try {
      const response = await this.http.post<ResponseInterface>(this.endpoints.frequentDrivers, { imei }).toPromise();

      if (response?.data?.drivers?.length) {
        return response.data.drivers.map((driver: any) => ({
          id: driver.driver_id.toString(),
          name: driver.driver_name || 'Conductor Desconocido',
          document: driver.cod_document || driver.cod_trab || "",
          image: driver.image ? (driver.image.startsWith('data:') ? driver.image : `data:image/jpeg;base64,${driver.image}`) : '',
          evaluated: true,
          frequency: driver.frequency,
          lastSeen: driver.last_seen
            ? (() => {
                const d = new Date(driver.last_seen.includes('T') ? driver.last_seen : driver.last_seen.replace(' ', 'T'));
                return !isNaN(d.getTime())
                  ? d.toLocaleString('es-PE', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false
                    })
                  : driver.last_seen;
              })()
            : '-'
        }));
      }

    } catch (error) {
      console.error('Error fetching evaluated drivers:', error);
    }

    return [];
  }

}

